import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { ChevronDown, ShoppingCart, Mail, Search, Menu, Trash2, Minus, Plus } from "lucide-react";
import "./App.css";
import { signInWithGoogle, signOutFromGoogle, onAuthChanged } from "./config/firebase-config";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib/dist/pdf-lib.esm.js";
import fontkit from "@pdf-lib/fontkit";
import {
  fetchProductsOnce,
  subscribeToProducts,
  createProductRecord,
  updateProductRecord,
  deleteProductRecord,
  uploadProductImageFile,
} from "./services/firebaseProducts";
import {
  createOrderRecord,
  fetchOrdersOnce,
  subscribeToOrders,
} from "./services/orders";
import {
  createMaterialRecord,
  fetchMaterialsOnce,
  subscribeToMaterials,
  updateMaterialRecord,
} from "./services/materials";

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "")
  .split(",")
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);
const isAdminEmail = (email) => {
  const normalized = (email || "").trim().toLowerCase();
  return Boolean(normalized) && ADMIN_EMAILS.includes(normalized);
};

const getApiBase = () => {
  const env = process.env.REACT_APP_API_BASE?.trim();
  if (env) return env.replace(/\/$/, "");
  if (typeof window !== "undefined") {
    // running dev server on e.g. :3005 while backend on :3001
    if (window.location.hostname === "localhost" && window.location.port !== "3001") {
      return "http://localhost:3001";
    }
  }
  return "";
};

const API_BASE = getApiBase();
const API_ORDERS_URL = API_BASE ? `${API_BASE}/api/orders` : "/api/orders";
// products endpoint is unused now (Firestore az elsődleges), de tartjuk a konstansokat kompatibilitásra
const API_PRODUCTS_URL = API_BASE ? `${API_BASE}/api/products` : "/api/products";
const API_CATEGORIES_URL = API_BASE ? `${API_BASE}/api/categories` : "/api/categories";

// ---- Default data (fallback) ----
const DEFAULT_CATEGORIES = [
  { id: "osszes", label: "Összes termék" },
  { id: "muanyag-kulcstarto", label: "Műanyag kulcstartó" },
  { id: "fem-kulcstarto", label: "Fém kulcstartó" },
  { id: "kituzo", label: "Kitűző" },
  { id: "nyaklany", label: "Nyaklánc" },
  { id: "gyuru", label: "Gyűrű" },
];

const fallbackImage = (seed) => `https://picsum.photos/seed/${seed}/800/600`;
const DEFAULT_PRODUCTS = [
  { id: "szakitas", name: "Szakítás", price: 2990, categoryId: "fem-kulcstarto", imageUrl: fallbackImage('szakitas') },
  { id: "matematikus", name: "Matematikus", price: 2990, categoryId: "fem-kulcstarto", imageUrl: fallbackImage('matematikus') },
  { id: "vilagutazo", name: "Világutazó", price: 2990, categoryId: "fem-kulcstarto", imageUrl: fallbackImage('vilagutazo') },
  { id: "zsolt", name: "Zsolt", price: 2990, categoryId: "fem-kulcstarto", imageUrl: fallbackImage('zsolt') },
  { id: "vadasz", name: "Vadász", price: 2490, categoryId: "muanyag-kulcstarto", imageUrl: fallbackImage('vadasz') },
  { id: "partiallat", name: "Partiállat", price: 2490, categoryId: "muanyag-kulcstarto", imageUrl: fallbackImage('partiallat') },
];

const COD_FEE = 500;
const BANK_ACCOUNT = process.env.REACT_APP_BANK_ACCOUNT || "00000000-00000000";

const formatHUF = (n) => new Intl.NumberFormat("hu-HU").format(n) + " Ft";

const fetchFontBytes = async () => {
  try {
    const res = await fetch("https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf");
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch (err) {
    console.warn("Nem sikerült betölteni a fontot PDF-hez, fallback Helvetica.", err);
    return null;
  }
};

const generateOrderPdf = async (order = {}) => {
  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);
  const fontBytes = await fetchFontBytes();
  const font = fontBytes
    ? await pdfDoc.embedFont(fontBytes)
    : await pdfDoc.embedFont(StandardFonts.Helvetica);

  let page = pdfDoc.addPage([595, 842]); // A4 portrait
  const { width, height } = page.getSize();
  const margin = 50;
  let y = height - margin;

  const titleSize = 24;
  page.drawText("MNGKP", { x: margin, y, size: titleSize, font, color: rgb(0.1, 0.1, 0.1) });
  const subTitle = "Rendelés visszaigazolás";
  page.drawText(subTitle, {
    x: margin + 90,
    y: y - 6,
    size: 14,
    font,
    color: rgb(0.35, 0.35, 0.35),
  });
  y -= 36;

  const infoLine = (label, value) => {
    page.drawText(label, { x: margin, y, size: 11, font, color: rgb(0.25, 0.25, 0.25) });
    page.drawText(value || "", {
      x: margin + 120,
      y,
      size: 11,
      font,
      color: rgb(0.05, 0.05, 0.05),
    });
    y -= 16;
  };

  infoLine("Rendelés azonosító:", order.id || "(n/a)");
  infoLine("E-mail:", order.email || "");
  const addressObj = order.address || {};
  const addressLine =
    addressObj.line ||
    [addressObj.zip, addressObj.city, addressObj.street, addressObj.house]
      .filter(Boolean)
      .join(" ");
  infoLine("Szállítási cím:", addressLine || "(nincs megadva)");
  y -= 8;

  page.drawText("Tételek", { x: margin, y, size: 13, font, color: rgb(0.15, 0.15, 0.15) });
  y -= 18;

  const headers = [
    { label: "Termék", width: 240 },
    { label: "Menny.", width: 60, align: "right" },
    { label: "Egységár", width: 100, align: "right" },
    { label: "Összesen", width: 100, align: "right" },
  ];

  let x = margin;
  headers.forEach((h) => {
    const textWidth = font.widthOfTextAtSize(h.label, 11);
    const drawX = h.align === "right" ? x + h.width - textWidth : x;
    page.drawText(h.label, { x: drawX, y, size: 11, font, color: rgb(0.3, 0.3, 0.3) });
    x += h.width;
  });
  y -= 14;

  const items = Array.isArray(order.items) ? order.items : [];
  items.forEach((it) => {
    if (y < margin + 60) {
      page = pdfDoc.addPage([595, 842]);
      y = height - margin;
    }
    x = margin;
    const qty = Number(it.qty || 1);
    const lineTotal = Number(it.lineTotal || it.price * qty || 0);
    const cells = [
      { text: it.name || "(tétel)", width: headers[0].width, align: "left" },
      { text: `${qty}`, width: headers[1].width, align: "right" },
      { text: formatHUF(it.price || 0), width: headers[2].width, align: "right" },
      { text: formatHUF(lineTotal), width: headers[3].width, align: "right" },
    ];
    cells.forEach((c) => {
      const textWidth = font.widthOfTextAtSize(c.text, 11);
      const drawX = c.align === "right" ? x + c.width - textWidth : x;
      page.drawText(c.text, { x: drawX, y, size: 11, font, color: rgb(0.05, 0.05, 0.05) });
      x += c.width;
    });
    y -= 14;
  });

  y -= 10;
  const subtotal = formatHUF(order.subtotal || 0);
  const total = formatHUF(order.total || 0);
  const paymentLine = order.payment?.method === "transfer" ? "Átutalás" : "Utánvét";
  infoLine("Fizetés módja:", paymentLine);
  infoLine("Részösszeg:", subtotal);
  page.drawText("Végösszeg:", {
    x: margin,
    y,
    size: 12,
    font,
    color: rgb(0.05, 0.05, 0.05),
  });
  page.drawText(total, {
    x: margin + 120,
    y,
    size: 12,
    font,
    color: rgb(0.05, 0.05, 0.05),
  });

  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: "application/pdf" });
};

// Cart helpers
const cartCount = (c) => Object.values(c).reduce((a, b) => a + b, 0);

// Tiny image placeholder component
const ImgPh = ({ label = "300×200" }) => (
  <div
    className="w-full aspect-square bg-gradient-to-br from-neutral-200 to-neutral-300 text-neutral-600 grid place-items-center rounded-xl"
    aria-hidden
  >
    <span className="text-sm">{label}</span>
  </div>
);

// Absztrakt háttérm
const BackgroundArt = () => (
  <div aria-hidden className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
    {/* Narancs tömeg jobb oldalt */}
    <div className="absolute right-[-10rem] bottom-[-10rem] w-[56rem] h-[56rem] rounded-full blur-3xl opacity-90 mix-blend-multiply bg-gradient-to-tr from-orange-300 via-orange-500 to-orange-700" />

    {/* Kisebb narancs derengés bal oldalt */}
    <div className="absolute left-[-6rem] top-1/3 w-64 h-64 rounded-full blur-3xl opacity-50 mix-blend-multiply bg-gradient-to-tr from-orange-200 to-orange-400" />

    {/* Tisza jel - piros/zöld hurkok fehér aláhúzással */}
    <svg className="absolute left-[-18rem] top-[-10rem] w-[90rem] h-[90rem] opacity-100" viewBox="0 0 1000 1000">
      <defs>
        <linearGradient id="tisza-red" x1="0" x2="1">
          <stop offset="0%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#b91c1c" />
        </linearGradient>
        <linearGradient id="tisza-green" x1="0" x2="1">
          <stop offset="0%" stopColor="#22c55e" />
          <stop offset="100%" stopColor="#15803d" />
        </linearGradient>
        <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" result="g" />
          <feMerge>
            <feMergeNode in="g" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path d="M 220 520 C 150 430, 150 300, 240 230 C 350 145, 540 190, 600 310" fill="none" stroke="#ffffff" strokeOpacity="0.95" strokeWidth="100" strokeLinecap="round"/>
      <path d="M 220 520 C 150 430, 150 300, 240 230 C 350 145, 540 190, 600 310" fill="none" stroke="url(#tisza-red)" strokeWidth="80" filter="url(#softGlow)" strokeLinecap="round"/>
      <path d="M 780 480 C 850 570, 850 700, 760 770 C 650 855, 460 810, 400 690" fill="none" stroke="#ffffff" strokeOpacity="0.95" strokeWidth="100" strokeLinecap="round"/>
      <path d="M 780 480 C 850 570, 850 700, 760 770 C 650 855, 460 810, 400 690" fill="none" stroke="url(#tisza-green)" strokeWidth="80" filter="url(#softGlow)" strokeLinecap="round"/>
      <path d="M 580 330 L 420 670" stroke="#ffffff" strokeOpacity="0.98" strokeWidth="26" strokeLinecap="round"/>
    </svg>

    {/* Fehér villám-sáv a tömegek között */}
    <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <path d="M 620 0 L 560 220 L 640 360 L 560 520 L 640 700 L 600 1000" stroke="white" strokeWidth="16" strokeOpacity="0.9" fill="none"/>
    </svg>

    {/* Bal oldali zöld/kék paca */}
    <div className="absolute left-24 bottom-[22rem] w-[16rem] h-[16rem] rounded-full blur-2xl opacity-80 mix-blend-multiply bg-gradient-to-br from-lime-300 to-lime-700" />
    <div className="absolute left-10 bottom-[14rem] w-[18rem] h-[18rem] rounded-full blur-2xl opacity-75 mix-blend-multiply bg-gradient-to-tr from-blue-400 via-sky-300 to-indigo-700" />

    {/* Jobb oldali zöld/kék paca a narancs mellett */}
    <div className="absolute right-64 bottom-60 w-[18rem] h-[18rem] rounded-full blur-2xl opacity-80 mix-blend-multiply bg-gradient-to-br from-lime-300 to-lime-700" />
    <div className="absolute right-28 bottom-[22rem] w-[22rem] h-[22rem] rounded-full blur-2xl opacity-75 mix-blend-multiply bg-gradient-to-tr from-blue-400 via-sky-300 to-indigo-700" />
    <div className="absolute right-8 bottom-[28rem] w-[14rem] h-[14rem] rounded-full blur-2xl opacity-70 mix-blend-multiply bg-gradient-to-tr from-yellow-300 to-pink-400" />

    {/* Finom fehér textúra */}
    <div
      className="absolute inset-0 opacity-15"
      style={{
        backgroundImage:
          "repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.15) 2px, transparent 2px, transparent 10px)",
      }}
    />
  </div>
);

// Dropdown
const Dropdown = ({ label, items, onSelect }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const openNow = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div className="dropdown relative">
      <button
        className="flex items-center gap-1 px-3 py-2 rounded-2xl hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
        onFocus={openNow}
        onBlur={scheduleClose}
      >
        <span className="font-medium">{label}</span>
        <ChevronDown className="size-4" aria-hidden />
      </button>
      {open && (
        <ul
          role="menu"
          className="dropdown-menu absolute z-50 mt-2 min-w-[220px] rounded-2xl border border-neutral-200 bg-white p-1 shadow-xl"
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
        >
          {items.map((it) => (
            <li key={it.id} role="none">
              <button
                role="menuitem"
                className="dropdown-item w-full text-left px-3 py-2 rounded-xl hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
                onClick={() => {
                  onSelect(it.id);
                  setOpen(false);
                }}
              >
                {it.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Modal for "Elérhetőség"
const ContactModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-4">
          <Mail className="size-5 text-neutral-700" />
          <h2 id="contact-title" className="text-xl font-semibold">Elérhetőség</h2>
        </div>
        <p className="text-neutral-700 mb-4">
          Írj nekünk: <a className="underline" href="mailto:hello@mngkp.hu">hello@mngkp.hu</a> vagy hívj: +36 30 123 4567
        </p>
        <button
          onClick={onClose}
          className="btn btn-primary px-4 py-2 rounded-xl bg-neutral-900 text-white hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
        >
          Bezár
        </button>
      </div>
    </div>
  );
};

// Order modal (email + shipping address with separate fields)
const OrderModal = ({ open, onClose, onSubmit, cartTotal = 0 }) => {
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [street, setStreet] = useState("");
  const [house, setHouse] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("transfer");
  useEffect(() => {
    if (open) {
      setPaymentMethod("transfer");
    }
  }, [open]);
  if (!open) return null;
  const handleSubmit = (e) => {
    e.preventDefault();
    const validEmail = /.+@.+\..+/.test(email);
    if (!validEmail) { alert("Kérjük, valós e-mail címet adjon meg."); return; }
    if (!/^\d{4}$/.test(zip)) { alert("Az irányítószám 4 számjegy legyen."); return; }
    if (!city.trim() || !street.trim() || !house.trim()) { alert("Kérjük, töltse ki a város, utca és házszám mezőket."); return; }
    const address = `${zip} ${city}, ${street} ${house}`;
    const order = { email, zip, city, street, house, address, paymentMethod };
    window.dispatchEvent(new CustomEvent('order-submitted', { detail: order }));
    onSubmit && onSubmit(order);
    onClose && onClose();
  };
  const paymentFee = paymentMethod === "cod" ? COD_FEE : 0;
  const finalTotal = cartTotal + paymentFee;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      onClick={onClose}
    >
      <form
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl grid gap-4"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 id="order-title" className="text-xl font-semibold">Megrendelés</h2>
        <label className="grid gap-1">
          <span className="text-sm text-neutral-700">E-mail cím</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
            placeholder="pelda@domain.hu"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="grid gap-1">
            <span className="text-sm text-neutral-700">Irányítószám</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 4))}
              required
              className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
              placeholder="1234"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm text-neutral-700">Város</span>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
              className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
              placeholder="Budapest"
            />
          </label>
          <label className="grid gap-1 col-span-2">
            <span className="text-sm text-neutral-700">Utca</span>
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              required
              className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
              placeholder="Fő utca"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm text-neutral-700">Házszám</span>
            <input
              type="text"
              value={house}
              onChange={(e) => setHouse(e.target.value)}
              required
              className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
              placeholder="12/A"
            />
          </label>
        </div>
        <div className="rounded-3xl border border-neutral-200 bg-neutral-50 p-4 grid gap-3">
          <div>
            <p className="text-sm font-semibold text-neutral-700">Fizetési mód</p>
            <p className="text-xs text-neutral-500">Válassz utánvétet vagy átutalást.</p>
          </div>
          <label className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-3">
            <input
              type="radio"
              name="payment"
              value="transfer"
              checked={paymentMethod === "transfer"}
              onChange={() => setPaymentMethod("transfer")}
              className="mt-1"
            />
            <div>
              <p className="font-semibold">Átutalás (bankszámla: {BANK_ACCOUNT})</p>
              <p className="text-sm text-neutral-600">Kérjük, a közleménybe írd be az e-mail címedet.</p>
            </div>
          </label>
          <label className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-3">
            <input
              type="radio"
              name="payment"
              value="cod"
              checked={paymentMethod === "cod"}
              onChange={() => setPaymentMethod("cod")}
              className="mt-1"
            />
            <div>
              <p className="font-semibold">Utánvét (+{formatHUF(COD_FEE)})</p>
              <p className="text-sm text-neutral-600">Fizetés készpénzben a futárnak kiszállításkor.</p>
            </div>
          </label>
          <div className="rounded-2xl bg-white border border-neutral-200 p-3 text-sm">
            <p className="flex items-center justify-between font-semibold">
              <span>Rendelés értéke</span>
              <span>{formatHUF(cartTotal)}</span>
            </p>
            {paymentFee > 0 && (
              <p className="flex items-center justify-between mt-1">
                <span>Utánvét díj</span>
                <span>+{formatHUF(paymentFee)}</span>
              </p>
            )}
            <p className="flex items-center justify-between mt-2 text-base font-bold">
              <span>Végösszeg</span>
              <span>{formatHUF(finalTotal)}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost px-4 py-2 rounded-xl border hover:bg-neutral-50">Mégse</button>
          <button type="submit" className="btn btn-primary px-4 py-2 rounded-xl bg-neutral-900 text-white hover:bg-neutral-800">Megrendelés leadása</button>
        </div>
      </form>
    </div>
  );
};

// Product card
const ProductCard = ({ id, name, price, onAdd, onView, imageLabel, imageUrl }) => (
  <article className="group rounded-3xl border border-neutral-200 bg-white hover:shadow-lg transition-shadow overflow-hidden">
    <div className="relative">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name}
          loading="lazy"
          className="w-full aspect-square object-cover"
        />
      ) : (
        <ImgPh label={imageLabel ?? "Termék"} />
      )}
      <div className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/25">
        <button
          onClick={() => onView(id)}
          className="px-4 py-2 rounded-xl bg-white text-neutral-900 shadow hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
        >
          Megtekintés
        </button>
      </div>
    </div>
    <div className="p-4 flex flex-col gap-2">
      <h3 className="text-base font-semibold text-neutral-900">{name}</h3>
      <p className="text-neutral-600">{formatHUF(price)}</p>
      <button
        onClick={onAdd}
        className="btn btn-primary mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-neutral-900 px-4 py-2 text-white hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
        aria-label={`${name} kosárba`}
      >
        <ShoppingCart className="size-4" aria-hidden />
        Kosárba
      </button>
    </div>
  </article>
);
// Header
const Header = ({
  categories = DEFAULT_CATEGORIES,
  searchValue = "",
  onSearch,
  onSelectCategory,
  onOpenContact,
  onGoHome,
  onGoCart,
  cartCount: count = 0,
  onLogin,
  onLogout,
  user,
  showAdmin,
  onAdminAccess,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const [inputValue, setInputValue] = useState(searchValue);
  const userName = user?.displayName || user?.email;

  const handleAuthClick = () => {
    if (user) {
      onLogout?.();
    } else {
      onLogin?.();
    }
  };

  useEffect(() => {
    setInputValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handlePointerDown = (e) => {
      const menuEl = menuRef.current;
      const btnEl = toggleBtnRef.current;
      if (!menuEl || !btnEl) return;
      if (!menuEl.contains(e.target) && !btnEl.contains(e.target)) {
        setMobileOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [mobileOpen]);
  return (
    <header className="header">
      <div className="container">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <button ref={toggleBtnRef} className="sm:hidden p-2 rounded-xl hover:bg-neutral-100" onClick={() => setMobileOpen((v) => !v)} aria-label="Menü">
              <Menu className="size-5" />
            </button>
            <button onClick={onGoHome} aria-label="Vissza a főoldalra" className="text-lg font-bold tracking-tight hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 rounded-xl px-1">MNGKP</button>
          </div>

          <nav className="hidden sm:flex items-center gap-3" aria-label="Fő navigáció">
            <Dropdown label="Termékek" items={categories} onSelect={onSelectCategory} />
            <button
              onClick={onOpenContact}
              className="btn btn-ghost px-3 py-2 rounded-2xl hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
            >
              Elérhetőség
            </button>
            {showAdmin && (
              <button
                type="button"
                onClick={onAdminAccess}
                className="btn btn-ghost px-3 py-2 rounded-2xl border border-neutral-200 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
              >
                Admin
              </button>
            )}
            <button
              onClick={handleAuthClick}
              className="btn btn-primary px-4 py-2 rounded-2xl bg-neutral-900 text-white hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
            >
              {user ? "Kijelentkezés" : "Bejelentkezés"}
            </button>
            {userName && (
              <span className="px-3 py-1 rounded-2xl bg-white/80 text-xs font-semibold text-neutral-800">
                {userName}
              </span>
            )}
          </nav>

          <div className="hidden sm:flex items-center gap-3">
            <form className="relative" onSubmit={(e) => { e.preventDefault(); onSearch?.(inputValue); }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-neutral-400" aria-hidden />
              <input
                type="search"
                placeholder="Keresés…"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-48 rounded-2xl border border-neutral-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400"
              />
            </form>
            <button
              aria-label="Kosár megnyitása"
              onClick={onGoCart}
              className="relative p-2 rounded-xl hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
            >
              <ShoppingCart className="size-5" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-neutral-900 text-white text-[11px] grid place-items-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

          {mobileOpen && (
          <div ref={menuRef} className="sm:hidden border-t border-neutral-200 py-3 flex items-center gap-3">
            <Dropdown label="Termékek" items={categories} onSelect={(id) => { setMobileOpen(false); onSelectCategory(id); }} />
            <button onClick={() => { setMobileOpen(false); onOpenContact(); }} className="btn btn-ghost px-3 py-2 rounded-2xl hover:bg-neutral-100">Elérhetőség</button>
            {showAdmin && (
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  onAdminAccess?.();
                }}
                className="btn btn-ghost px-3 py-2 rounded-2xl border border-neutral-200 hover:bg-neutral-100"
              >
                Admin
              </button>
            )}
            <button
              onClick={() => {
                setMobileOpen(false);
                handleAuthClick();
              }}
              className="btn btn-primary px-3 py-2 rounded-2xl bg-neutral-900 text-white hover:bg-neutral-800"
            >
              {user ? "Kijelentkezés" : "Bejelentkezés"}
            </button>
            <button onClick={() => { setMobileOpen(false); onGoCart && onGoCart(); }} aria-label="Kosár" className="p-2 rounded-xl hover:bg-neutral-100">
              <ShoppingCart className="size-5" />
              {count > 0 && <span className="ml-1 text-xs">({count})</span>}
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

// Home (Landing)
const Home = () => (
  <main className="relative z-10 container">
    <section className="py-14 sm:py-20 text-center">
      <h1 className="hero-title text-2xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
        <span className="block">MAGYAR NEMZETI GLOBALISTA</span>
        <span className="block">KOMMUNISTA PIACTÉR</span>
      </h1>
      <p className="hero-subtitle mt-4 text-base sm:text-lg max-w-2xl mx-auto">
        Mindig annak szurkolunk, aki nyerésre áll.
      </p>
    </section>
  </main>
);

// Product Listing (Category)
const ProductListing = ({
  categoryId,
  categories = DEFAULT_CATEGORIES,
  products = [],
  searchTerm = "",
  onSelectCategory,
  onViewProduct,
  onAddToCart,
}) => {
  const currentCategory = useMemo(
    () => categories.find((c) => c.id === categoryId),
    [categories, categoryId]
  );
  const title = currentCategory?.label ?? "Termékek";
  const imageLabel = currentCategory?.label ?? "Termék";
  const otherCategories = useMemo(
    () => categories.filter((c) => c.id !== "osszes"),
    [categories]
  );
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const q = searchTerm.toLowerCase();
    return products.filter((p) => p.name?.toLowerCase().includes(q));
  }, [products, searchTerm]);
  const hasSearch = Boolean(searchTerm);

  return (
    <main className="container py-10 relative z-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-outline">{title}</h1>
        <div className="text-base font-medium text-neutral-800 flex flex-wrap items-center gap-2 rounded-2xl bg-white/95 px-4 py-2 shadow">
          <button onClick={() => onSelectCategory("osszes")} className="underline">
            Összes
          </button>
          {otherCategories.map((cat) => (
            <React.Fragment key={cat.id}>
              <span aria-hidden className="px-1 text-xl leading-none">•</span>
              <button onClick={() => onSelectCategory(cat.id)} className="underline">
                {cat.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {hasSearch && (
        <p className="text-base text-neutral-500 mb-4 text-center sm:text-left text-outline">
          Keresés: <span className="font-semibold">"{searchTerm}"</span> - {filteredProducts.length} találat
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3 text-outline">
        <label className="text-base font-semibold">Rendezés:</label>
        <select className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-neutral-400">
          <option>Legnépszerűbb</option>
          <option>Legolcsóbb</option>
          <option>Legdrágább</option>
        </select>
      </div>

      <section aria-label="Termékek" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredProducts.length === 0 ? (
          <div className="col-span-full text-base font-semibold text-neutral-600 text-outline">
            {hasSearch ? "Erre a keresésre nincs találat." : "Ebben a kategóriában még nincsenek termékek."}
          </div>
        ) : (
          filteredProducts.map((p) => (
            <ProductCard
              id={p.id}
              key={p.id}
              name={p.name}
              price={p.price}
              onAdd={() => { onAddToCart(p.id); alert(`${p.name} a kosárba került`); }}
              imageLabel={imageLabel}
              imageUrl={p.imageUrl}
              onView={onViewProduct}
            />
          ))
        )}
      </section>
    </main>
  );
};

// Product Detail
const ProductDetail = ({ product, onBack, onAdd }) => {
  return (
    <main className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6 py-10">
      <button onClick={onBack} className="mb-6 text-sm underline text-outline">Vissza</button>
      {!product ? (
        <div className="text-neutral-600 text-outline">A termék nem található.</div>
      ) : (
        <article className="grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-neutral-200 bg-white p-3">
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt={product.name}
                className="w-full h-full object-cover rounded-2xl"
              />
            ) : (
              <ImgPh label="Termékkép" />
            )}
          </div>
          <div className="flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-outline">{product.name}</h1>
            <p className="text-neutral-600 text-outline">{formatHUF(product.price)}</p>
            <p className="text-sm text-neutral-700 text-outline">Részletes leírás helye. Anyag, méret, súly, szállítási információk, stb.</p>
            <div className="flex gap-3">
              <button onClick={onAdd} className="btn btn-primary rounded-xl bg-neutral-900 text-white px-4 py-2 hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400">Kosárba</button>
            </div>
          </div>
        </article>
      )}
    </main>
  );
};

// Admin panel (in-app)
const AdminPanel = ({
  products = [],
  categories = DEFAULT_CATEGORIES,
  onBack,
  onHome,
  user,
  isAdmin,
}) => {
  const [form, setForm] = useState({
    name: "",
    price: "",
    categoryId: categories[0]?.id || "osszes",
    imageUrl: "",
    imagePath: "",
    materials: [],
  });
  const [adminView, setAdminView] = useState("menu"); // menu | products | orders | materials
  const [orders, setOrders] = useState([]);
  const [ordersStatus, setOrdersStatus] = useState("");
  const [materials, setMaterials] = useState([]);
  const [materialsStatus, setMaterialsStatus] = useState("");
  const [materialForm, setMaterialForm] = useState({
    name: "",
    unitPrice: "",
    sourceUrl: "",
  });
  const [materialStatus, setMaterialStatus] = useState("");
  const [materialError, setMaterialError] = useState("");
  const [materialBusy, setMaterialBusy] = useState(false);
  const [materialEditingId, setMaterialEditingId] = useState(null);
  const [materialSelect, setMaterialSelect] = useState("");
  const materialsById = useMemo(() => {
    const map = new Map();
    materials.forEach((m) => map.set(m.id, m));
    return map;
  }, [materials]);

  const sanitizeMaterialSelection = (list = []) => {
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    const result = [];
    list.forEach((item) => {
      if (!item) return;
      const id = (item.id ?? item).toString?.().trim?.() || "";
      if (!id || seen.has(id)) return;
      const qtyRaw = Number(item.qty ?? 1);
      const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? Math.floor(qtyRaw) : 1;
      seen.add(id);
      result.push({ id, qty });
    });
    return result;
  };

  const getMaterialQty = (id) => {
    const entry = (form.materials || []).find((m) => m.id === id);
    return entry?.qty || "";
  };

  const setMaterialQty = (id, qtyValue) => {
    if (!id) return;
    const parsed = Number(qtyValue);
    setForm((prev) => {
      const current = Array.isArray(prev.materials) ? prev.materials : [];
      const filtered = current.filter((m) => m && m.id !== id);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return { ...prev, materials: filtered };
      }
      return { ...prev, materials: [...filtered, { id, qty: Math.floor(parsed) }] };
    });
  };

  const addMaterialSelection = () => {
    const id = materialSelect;
    if (!id) return;
    setForm((prev) => {
      const current = Array.isArray(prev.materials) ? prev.materials : [];
      const exists = current.find((m) => m.id === id);
      if (exists) {
        return {
          ...prev,
          materials: current.map((m) =>
            m.id === id ? { ...m, qty: (m.qty || 1) + 1 } : m
          ),
        };
      }
      return { ...prev, materials: [...current, { id, qty: 1 }] };
    });
    setMaterialSelect("");
  };

  const removeMaterialSelection = (id) => {
    setForm((prev) => {
      const current = Array.isArray(prev.materials) ? prev.materials : [];
      return { ...prev, materials: current.filter((m) => m.id !== id) };
    });
  };
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const catOptions = categories.length ? categories : DEFAULT_CATEGORIES;

  useEffect(() => {
    if (editingId) {
      const hit = products.find((p) => p.id === editingId);
      if (hit) {
        setForm({
          name: hit.name || "",
          price: hit.price || "",
          categoryId: hit.categoryId || catOptions[0]?.id || "osszes",
          imageUrl: hit.imageUrl || "",
          imagePath: hit.imagePath || "",
          materials: sanitizeMaterialSelection(hit.materials),
        });
      }
    }
  }, [editingId, products, catOptions]);

  const resetForm = (message = "") => {
    setEditingId(null);
    setForm({
      name: "",
      price: "",
      categoryId: catOptions[0]?.id || "osszes",
      imageUrl: "",
      imagePath: "",
      materials: [],
    });
    if (message) setStatus(message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin || !user) {
      setError("Csak admin e-maillel jelentkezve lehet menteni.");
      return;
    }
    setError("");
    setStatus("");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        price: Number(form.price),
        categoryId: form.categoryId,
        imageUrl: form.imageUrl.trim(),
        imagePath: form.imagePath,
        materials: sanitizeMaterialSelection(form.materials),
      };
      if (!payload.name) throw new Error("A név kötelező.");
      if (!payload.price || payload.price <= 0) throw new Error("Az ár legyen pozitív szám.");
      if (editingId) {
        await updateProductRecord(editingId, payload);
        setStatus("Termék frissítve.");
      } else {
        await createProductRecord(payload);
        setStatus("Termék rögzítve.");
      }
      resetForm();
    } catch (err) {
      setError(err.message || "Mentési hiba");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (id) => {
    setEditingId(id);
    setStatus("");
    setError("");
  };

  const handleDelete = async (id, imagePath) => {
    if (!isAdmin || !user) {
      setError("Csak admin e-maillel jelentkezve lehet törölni.");
      return;
    }
    if (!window.confirm("Biztosan törlöd a terméket?")) return;
    setBusy(true);
    setError("");
    setStatus("");
    try {
      await deleteProductRecord(id, { imagePath });
      setStatus("Termék törölve.");
      if (editingId === id) resetForm();
    } catch (err) {
      setError(err.message || "Törlés sikertelen");
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    setStatus("Feltöltés folyamatban...");
    try {
      const { downloadURL, storagePath } = await uploadProductImageFile(file);
      setForm((f) => ({ ...f, imageUrl: downloadURL, imagePath: storagePath }));
      setStatus("Kép feltöltve és URL frissítve.");
    } catch (err) {
      setError(err.message || "Feltöltés hiba");
    } finally {
      setUploading(false);
    }
  };

  const resetMaterialForm = (message = "") => {
    setMaterialEditingId(null);
    setMaterialForm({
      name: "",
      unitPrice: "",
      sourceUrl: "",
    });
    if (message) setMaterialStatus(message);
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin || !user) {
      setMaterialError("Csak admin e-maillel jelentkezve lehet menteni.");
      return;
    }
    setMaterialError("");
    setMaterialStatus("");
    setMaterialBusy(true);
    try {
      const payload = {
        name: materialForm.name.trim(),
        unitPrice: materialForm.unitPrice,
        sourceUrl: materialForm.sourceUrl.trim(),
      };
      if (materialEditingId) {
        await updateMaterialRecord(materialEditingId, payload);
        resetMaterialForm("Anyag frissítve.");
      } else {
        await createMaterialRecord(payload);
        resetMaterialForm("Anyag mentve.");
      }
    } catch (err) {
      setMaterialError(err.message || "Mentesi hiba");
    } finally {
      setMaterialBusy(false);
    }
  };

  const loadMaterials = useCallback(async () => {
    try {
      setMaterialsStatus("Anyagok betoltese...");
      const list = await fetchMaterialsOnce();
      setMaterials(list);
      setMaterialsStatus(`Osszes anyag: ${list.length}`);
    } catch (err) {
      setMaterialsStatus(err.message || "Anyagok betoltese sikertelen");
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      setOrdersStatus("Rendelések betöltése...");
      const list = await fetchOrdersOnce();
      setOrders(list);
      setOrdersStatus(`Összes rendelés: ${list.length}`);
    } catch (err) {
      setOrdersStatus(err.message || "Rendelések betöltése sikertelen");
    }
  }, []);

  useEffect(() => {
    if (adminView !== "materials" && adminView !== "products") return;
    let unsub = null;
    (async () => {
      try {
        await loadMaterials();
        unsub = subscribeToMaterials(
          (latest) => {
            setMaterials(latest);
            setMaterialsStatus(`Osszes anyag: ${latest.length}`);
          },
          (err) => setMaterialsStatus(err.message || "Feliratkozas hiba az anyagokra")
        );
      } catch (err) {
        setMaterialsStatus(err.message || "Anyagok betoltese sikertelen");
      }
    })();
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [adminView, loadMaterials]);

  const startMaterialEdit = (id) => {
    const hit = materials.find((m) => m.id === id);
    if (!hit) return;
    setMaterialEditingId(id);
    setMaterialForm({
      name: hit.name || "",
      unitPrice: hit.unitPrice || "",
      sourceUrl: hit.sourceUrl || "",
    });
    setMaterialStatus("");
    setMaterialError("");
  };

  useEffect(() => {
    if (adminView !== "orders") return;
    let unsub = null;
    (async () => {
      try {
        const list = await fetchOrdersOnce();
        setOrders(list);
        setOrdersStatus(`Összes rendelés: ${list.length}`);
        unsub = subscribeToOrders(
          (latest) => {
            setOrders(latest);
            setOrdersStatus(`Összes rendelés: ${latest.length}`);
          },
          (err) => setOrdersStatus(err.message || "Rendelések feliratkozás hiba")
        );
      } catch (err) {
        setOrdersStatus(err.message || "Rendelések betöltése sikertelen");
      }
    })();
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [adminView]);

  const renderMenu = () => (
    <main className="container py-16 relative z-10">
      <div className="flex items-center justify-between mb-8">
        <button onClick={onBack} className="underline text-sm">
          Vissza
        </button>
        <button onClick={onHome} className="underline text-sm">
          Főoldal
        </button>
      </div>
      <div className="grid gap-6 max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-center">Admin</h1>
        <p className="text-center text-neutral-600">
          Válassz modulokat: Termékek karbantartása vagy feladott rendelések.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              setAdminView("materials");
              loadMaterials();
            }}
            className="rounded-3xl border border-neutral-200 bg-white shadow hover:shadow-lg transition p-8 text-center text-xl font-semibold"
          >
            Anyagok
            <div className="text-sm text-neutral-500 mt-2">Beszerzési lista, egységár, link</div>
          </button>
          <button
            onClick={() => setAdminView("products")}
            className="rounded-3xl border border-neutral-200 bg-white shadow hover:shadow-lg transition p-8 text-center text-xl font-semibold"
          >
            Termékek
            <div className="text-sm text-neutral-500 mt-2">Karbantartás, feltöltés, szerkesztés</div>
          </button>
          <button
            onClick={() => {
              setAdminView("orders");
              loadOrders();
            }}
            className="rounded-3xl border border-neutral-200 bg-white shadow hover:shadow-lg transition p-8 text-center text-xl font-semibold"
          >
            Rendelések
            <div className="text-sm text-neutral-500 mt-2">Feladott rendelések listája</div>
          </button>
        </div>
      </div>
    </main>
  );

  if (!isAdmin || !user) {
    return (
      <main className="container py-10 relative z-10">
        <button onClick={onBack} className="underline text-sm mb-4">
          Vissza
        </button>
        <div className="rounded-3xl border border-neutral-200 bg-white p-6 shadow">
          <h1 className="text-2xl font-bold mb-2">Admin felület</h1>
          <p>Jelentkezz be az admin e-mail címmel a használathoz.</p>
        </div>
      </main>
    );
  }

  if (adminView === "menu") return renderMenu();

  if (adminView === "orders") {
    return (
      <main className="container py-10 relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setAdminView("menu")} className="underline text-sm">
              Vissza a menübe
            </button>
            <button onClick={onHome} className="underline text-sm">
              Főoldal
            </button>
          </div>
          <div className="text-sm text-neutral-600">Bejelentkezve: {user.email}</div>
        </div>

        <div className="rounded-3xl border border-neutral-200 bg-white p-6 shadow grid gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Rendelések</h2>
            <div className="flex items-center gap-3">
              <button onClick={loadOrders} className="text-sm underline">
                Frissítés
              </button>
              <button onClick={() => setAdminView("products")} className="text-sm underline">
                Ugrás a termékekhez
              </button>
            </div>
          </div>
          <div className="text-sm text-neutral-600">{ordersStatus || "–"}</div>
          <div className="grid gap-3">
            {orders.map((o) => (
              <div
                key={o.id}
                className="rounded-2xl border border-neutral-200 p-4 bg-neutral-50"
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold">#{o.id || "(n/a)"}</div>
                  <div className="text-sm text-neutral-600">{o.status || "uj"}</div>
                </div>
                <div className="text-sm text-neutral-700 mt-1">
                  {o.email || "nincs e-mail"} · {o.address?.line || o.address || ""}
                </div>
                {o.total && (
                  <div className="text-sm text-neutral-700 font-semibold mt-1">
                    Összesen: {formatHUF(o.total)}
                  </div>
                )}
              </div>
            ))}
            {orders.length === 0 && (
              <div className="text-sm text-neutral-600">Nincs rendelés vagy nincs jogosultság a listázáshoz.</div>
            )}
          </div>
        </div>
      </main>
    );
  }

  if (adminView === "materials") {
    return (
      <main className="container py-10 relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setAdminView("menu")} className="underline text-sm">
              Vissza a menübe
            </button>
            <button onClick={onHome} className="underline text-sm">
              Főoldal
            </button>
          </div>
          <div className="text-sm text-neutral-600">Bejelentkezve: {user.email}</div>
        </div>
  
        <div className="grid md:grid-cols-[1.05fr_1.15fr] gap-6">
          <form
            onSubmit={handleMaterialSubmit}
            className="rounded-3xl border border-neutral-200 bg-white p-6 shadow grid gap-4"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {materialEditingId ? "Anyag szerkesztése" : "Anyag felvétele"}
              </h2>
              {materialEditingId ? (
                <button
                  type="button"
                  className="text-sm underline"
                  onClick={() => resetMaterialForm("Szerkesztés megszakítva.")}
                >
                  Mégse
                </button>
              ) : (
                <button
                  type="button"
                  className="text-sm underline"
                  onClick={() => resetMaterialForm("Űrlap törölve.")}
                >
                  Újrakezdés
                </button>
              )}
            </div>
  
            <label className="grid gap-1">
              <span className="text-sm text-neutral-700">Név</span>
              <input
                type="text"
                value={materialForm.name}
                onChange={(e) => setMaterialForm((f) => ({ ...f, name: e.target.value }))}
                required
                className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
              />
            </label>
  
            <label className="grid gap-1">
              <span className="text-sm text-neutral-700">Egységár (Ft)</span>
              <input
                type="number"
                min="1"
                value={materialForm.unitPrice}
                onChange={(e) => setMaterialForm((f) => ({ ...f, unitPrice: e.target.value }))}
                required
                className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
              />
            </label>
  
            <label className="grid gap-1">
              <span className="text-sm text-neutral-700">Beszerzési link</span>
              <input
                type="url"
                value={materialForm.sourceUrl}
                onChange={(e) => setMaterialForm((f) => ({ ...f, sourceUrl: e.target.value }))}
                placeholder="https://..."
                className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
              />
            </label>
  
            {materialError && <div className="text-sm text-red-600">{materialError}</div>}
            {materialStatus && <div className="text-sm text-green-700">{materialStatus}</div>}
  
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={materialBusy}
                className="btn btn-primary px-4 py-2 rounded-xl bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-70"
              >
                {materialEditingId ? "Mentés" : "Hozzáadás"}
              </button>
              {materialBusy && <span className="text-sm text-neutral-500">Mentem...</span>}
            </div>
          </form>

          <section className="rounded-3xl border border-neutral-200 bg-white p-6 shadow">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-semibold">Anyag lista</h2>
              <button onClick={loadMaterials} className="text-sm underline">Frissítés</button>
            </div>
            <div className="text-sm text-neutral-600 mb-3">{materialsStatus || "-"}</div>
            <div className="grid gap-3">
              {materials.map((m) => (
                <div
                  key={m.id}
                  className="grid md:grid-cols-[1fr_auto_auto_auto] items-center gap-3 rounded-2xl border border-neutral-200 px-4 py-3 bg-neutral-50"
                >
                  <div>
                    <div className="font-semibold">{m.name || "(név nélkül)"}</div>
                    <div className="text-sm text-neutral-600">Egységár: {formatHUF(m.unitPrice)}</div>
                  </div>
                  {m.sourceUrl ? (
                    <a
                      href={m.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm underline text-blue-700 break-all"
                    >
                      Forrás
                    </a>
                  ) : (
                    <span className="text-xs text-neutral-500">Nincs link</span>
                  )}
                  <button
                    type="button"
                    onClick={() => startMaterialEdit(m.id)}
                    className="text-sm underline justify-self-end"
                  >
                    Szerkesztés
                  </button>
                </div>
              ))}
              {materials.length === 0 && (
                <div className="text-sm text-neutral-600">Még nincs felvitt anyag.</div>
              )}
            </div>
          </section>
        </div>
      </main>
    );
  }
  

  return (
    <main className="container py-10 relative z-10">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => setAdminView("menu")} className="underline text-sm">
            Vissza a menübe
          </button>
          <button onClick={onHome} className="underline text-sm">
            Főoldal
          </button>
        </div>
        <div className="text-sm text-neutral-600">Bejelentkezve: {user.email}</div>
      </div>

      <div className="grid gap-6">
        <form
          onSubmit={handleSubmit}
          className="rounded-3xl border border-neutral-200 bg-white p-6 shadow grid gap-4"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {editingId ? "Termék szerkesztése" : "Új termék felvétele"}
            </h2>
            {editingId && (
              <button
                type="button"
                className="text-sm underline"
                onClick={() => resetForm("Szerkesztés megszakítva.")}
              >
                Mégse
              </button>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="grid gap-1">
              <span className="text-sm text-neutral-700">Név</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
                className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
              />
            </label>

            <label className="grid gap-1">
              <span className="text-sm text-neutral-700">Ár (Ft)</span>
              <input
                type="number"
                min="1"
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                required
                className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
              />
            </label>
          </div>

          <label className="grid gap-1">
            <span className="text-sm text-neutral-700">Kategória</span>
            <select
              value={form.categoryId}
              onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
              className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
            >
              {catOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-1">
            <span className="text-sm text-neutral-700">Kép URL (opcionális)</span>
            <input
              type="text"
              value={form.imageUrl}
              onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
              placeholder="https://..."
              className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
              />
            </label>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="grid gap-1">
              <span className="text-sm text-neutral-700">Kép feltöltése (Storage)</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                disabled={uploading}
              />
              {uploading && <span className="text-xs text-neutral-500">Feltöltés...</span>}
              {form.imageUrl && (
                <div className="mt-2 inline-flex items-center gap-2 text-sm text-neutral-600">
                  <div className="w-14 h-14 rounded-lg overflow-hidden border border-neutral-200 bg-neutral-100">
                    <img src={form.imageUrl} alt="Előnézet" className="w-full h-full object-cover" />
                  </div>
                  <span>Előnézet</span>
                </div>
              )}
            </label>

            <div className="grid gap-2">
              <div className="text-sm text-neutral-700">Alapanyagok</div>
              <div className="flex flex-col gap-2 rounded-2xl border border-neutral-200 bg-neutral-50 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={materialSelect}
                    onChange={(e) => setMaterialSelect(e.target.value)}
                    className="rounded-xl border border-neutral-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400 min-w-48"
                  >
                    <option value="">Válassz anyagot...</option>
                    {materials.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name || "(név nélkül)"}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={addMaterialSelection}
                    className="btn btn-ghost px-3 py-2 rounded-xl border border-neutral-200 hover:bg-white"
                    disabled={!materialSelect}
                  >
                    Hozzáadás
                  </button>
                </div>

                <div className="max-h-48 overflow-auto grid gap-2">
                  {Array.isArray(form.materials) && form.materials.length > 0 ? (
                    form.materials.map((m) => {
                      const mat = materialsById.get(m.id);
                      const qty = getMaterialQty(m.id);
                      return (
                        <div
                          key={m.id}
                          className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm"
                        >
                          <div className="grid">
                            <span className="font-medium">{mat?.name || "(név nélkül)"}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-neutral-600">Mennyiség</label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={qty}
                              onChange={(e) => setMaterialQty(m.id, e.target.value)}
                              className="w-20 rounded-lg border border-neutral-200 px-2 py-1 text-right"
                            />
                            <button
                              type="button"
                              onClick={() => removeMaterialSelection(m.id)}
                              className="text-xs text-red-700 underline"
                            >
                              Törlés
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-sm text-neutral-500">Nincs hozzárendelt anyag.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
          {status && <div className="text-sm text-green-700">{status}</div>}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={busy}
              className="btn btn-primary px-4 py-2 rounded-xl bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-70"
            >
              {editingId ? "Mentés" : "Hozzáadás"}
            </button>
            {busy && <span className="text-sm text-neutral-500">Dolgozom...</span>}
          </div>
        </form>

        <section className="rounded-3xl border border-neutral-200 bg-white p-6 shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Termékek</h2>
            <span className="text-sm text-neutral-500">{products.length} db</span>
          </div>
          <div className="grid gap-3">
            {products.map((p) => {
              const materialNames = Array.isArray(p.materials)
                ? p.materials
                    .map((item) => {
                      const id = item?.id ?? item;
                      const mat = materialsById.get(id);
                      if (!mat) return null;
                      const qty = Number(item?.qty ?? 1);
                      const qtyLabel = Number.isFinite(qty) && qty > 1 ? ` (${qty}x)` : "";
                      return `${mat.name}${qtyLabel}`;
                    })
                    .filter(Boolean)
                : [];
              const materialCost = Array.isArray(p.materials)
                ? p.materials.reduce((sum, item) => {
                    const id = item?.id ?? item;
                    const mat = materialsById.get(id);
                    if (!mat) return sum;
                    const qty = Number(item?.qty ?? 1);
                    const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
                    return sum + mat.unitPrice * safeQty;
                  }, 0)
                : 0;
              return (
                <div
                  key={p.id}
                  className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 rounded-2xl border border-neutral-200 px-4 py-3"
                >
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-neutral-100 border border-neutral-200 flex items-center justify-center">
                    {p.imageUrl ? (
                      <img
                        src={p.imageUrl}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-[11px] text-neutral-500 text-center px-1">Nincs kép</span>
                    )}
                  </div>
                  <div>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-sm text-neutral-600">
                      {formatHUF(p.price)} · {p.categoryId}
                    </div>
                    <div className="text-xs text-neutral-600">
                      Költség: {materialCost > 0 ? formatHUF(materialCost) : "0 Ft"}
                    </div>
                    {materialNames.length > 0 && (
                      <div className="text-xs text-neutral-600 mt-1">Anyagok: {materialNames.join(", ")}</div>
                    )}
                  </div>
                  <button
                    onClick={() => startEdit(p.id)}
                    className="text-sm underline"
                  >
                    Szerkesztés
                  </button>
                  <button
                    onClick={() => handleDelete(p.id, p.imagePath)}
                    className="text-sm text-red-700 underline"
                    disabled={busy}
                  >
                    Törlés
                  </button>
                </div>
              );
            })}
            {products.length === 0 && (
              <div className="text-sm text-neutral-600">Nincs termék.</div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

// Cart screen
const CartScreen = ({
  items = [],
  total,
  onBack,
  onInc,
  onDec,
  onRemove,
  onClear,
  onOrder,
  onDownloadPdf,
  canDownloadPdf,
  bankAccountInfo,
  lastOrderReady,
}) => {
  const entries = items;
  return (
    <main className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 py-10">
      <button onClick={onBack} className="mb-6 text-sm underline text-outline">Vissza</button>
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-outline">Kosár</h1>
      {entries.length === 0 ? (
        <div className="grid gap-3">
          <p className="text-neutral-600 text-outline">A kosarad üres.</p>
          {lastOrderReady && canDownloadPdf && (
            <button
              onClick={onDownloadPdf}
              className="inline-flex w-fit items-center gap-2 px-4 py-2 rounded-full bg-white border border-neutral-200 shadow-sm hover:bg-neutral-50"
            >
              Legutóbbi rendelés PDF letöltése
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {entries.map(({ id, qty, prod }) => (
            <div key={id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-4">
              <div>
                <div className="font-medium">{prod.name}</div>
                <div className="text-sm text-neutral-600">{formatHUF(prod.price)}</div>
              </div>
              <div className="flex items-center gap-2">
                <button aria-label="Csökkentés" onClick={() => onDec(id)} className="p-2 rounded-lg border hover:bg-neutral-50"><Minus className="size-4"/></button>
                <span className="w-8 text-center">{qty}</span>
                <button aria-label="Növelés" onClick={() => onInc(id)} className="p-2 rounded-lg border hover:bg-neutral-50"><Plus className="size-4"/></button>
              </div>
              <div className="font-medium">{formatHUF(prod.price * qty)}</div>
              <button aria-label="Eltávolítás" onClick={() => onRemove(id)} className="p-2 rounded-lg border hover:bg-neutral-50"><Trash2 className="size-4"/></button>
            </div>
          ))}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-lg font-semibold text-outline">Összesen: {formatHUF(total)}</div>
            <div className="flex gap-3">
              <button onClick={onClear} className="btn btn-ghost px-4 py-2 rounded-xl border hover:bg-neutral-50">Kosár ürítése</button>
              {lastOrderReady && canDownloadPdf && (
                <button onClick={onDownloadPdf} className="btn btn-ghost px-4 py-2 rounded-xl border hover:bg-neutral-50">PDF letöltése</button>
              )}
              <button onClick={onOrder} className="btn btn-primary px-4 py-2 rounded-xl bg-neutral-900 text-white hover:bg-neutral-800">Megrendelés</button>
            </div>
          </div>
          {lastOrderReady && bankAccountInfo?.id && (
            <div className="mt-3 rounded-2xl border border-neutral-200 bg-white px-4 py-3 shadow-sm text-sm text-neutral-800">
              <div className="font-semibold text-neutral-900 mb-1">Átutalási adatok</div>
              <div>Bankszámla: <span className="font-semibold">{bankAccountInfo.bankAccount || "nincs megadva"}</span></div>
              <div>Közlemény: <span className="font-semibold">{bankAccountInfo.id}</span></div>
            </div>
          )}
        </div>
      )}
    </main>
  );
};

export default function App() {
  const [route, setRoute] = useState({ name: "home" });
  const [, setStack] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [lastOrderPdf, setLastOrderPdf] = useState(null);
  const [lastOrderInfo, setLastOrderInfo] = useState(null);
  const [lastOrderReady, setLastOrderReady] = useState(false);
  const [cart, setCart] = useState({});
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [products, setProducts] = useState(DEFAULT_PRODUCTS);
  const [user, setUser] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const handleSearchSubmit = useCallback(
    (value) => {
      const trimmed = value.trim();
      setSearchTerm(trimmed);
      if (route.name !== "cat" || route.id !== "osszes") {
        setStack((s) => [...s, route]);
        setRoute({ name: "cat", id: "osszes" });
      }
    },
    [route]
  );
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthChanged((firebaseUser) => {
      setUser(firebaseUser);
      const email = (firebaseUser?.email || "").toLowerCase();
      setIsAdminUser(isAdminEmail(email));
    });
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

  const handleGoogleLogin = useCallback(async () => {
    try {
      const res = await signInWithGoogle();
      const loggedInUser = res?.user;
      if (loggedInUser) {
        setUser(loggedInUser);
        setIsAdminUser(isAdminEmail(loggedInUser.email));
      }
    } catch (err) {
      console.error("Google bejelentkezés sikertelen:", err);
      alert("A Google bejelentkezés nem sikerült. Ellenőrizd a beállításokat vagy próbáld újra később.");
    }
  }, []);

  const handleGoogleLogout = useCallback(async () => {
    try {
      await signOutFromGoogle();
      setIsAdminUser(false);
    } catch (err) {
      console.error("Kijelentkezés sikertelen:", err);
      alert("A kijelentkezés nem sikerült, kérjük próbáld újra.");
    }
  }, []);

  const handleAdminAccess = useCallback(() => {
    setStack((s) => [...s, route]);
    setRoute({ name: "admin" });
  }, [route]);

  useEffect(() => {
    let ignore = false;
    const fetchCategories = async () => {
      try {
        const res = await fetch(API_CATEGORIES_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (Array.isArray(data.categories) && !ignore) {
          setCategories(data.categories);
        }
      } catch (err) {
        console.warn("Nem sikerült betölteni a kategóriákat, fallback a beépített listára:", err);
        if (!ignore) setCategories(DEFAULT_CATEGORIES);
      }
    };
    fetchCategories();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (lastOrderPdf?.url) {
        URL.revokeObjectURL(lastOrderPdf.url);
      }
    };
  }, [lastOrderPdf]);

  useEffect(() => {
    let ignore = false;
    let unsubscribe = null;

    const boot = async () => {
      try {
        const initial = await fetchProductsOnce();
        if (!ignore) setProducts(initial);
        unsubscribe = subscribeToProducts(
          (list) => {
            if (!ignore) setProducts(list);
          },
          (err) => {
            console.warn("[firebase] Termék feliratkozás hiba:", err);
          }
        );
      } catch (err) {
        console.warn("[firebase] Termék betöltés sikertelen:", err);
        if (!ignore) setProducts([]);
      }
    };

    boot();

    return () => {
      ignore = true;
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  const productsById = useMemo(() => {
    const map = new Map();
    products.forEach((p) => map.set(p.id, p));
    return map;
  }, [products]);

  const getProductsForCategory = useCallback(
    (categoryId) => {
      if (!categoryId || categoryId === "osszes") return products;
      return products.filter((p) => p.categoryId === categoryId);
    },
    [products]
  );

  const cartEntries = useMemo(
    () =>
      Object.entries(cart)
        .map(([id, qty]) => ({ id, qty, prod: productsById.get(id) }))
        .filter((entry) => !!entry.prod),
    [cart, productsById]
  );

  const cartTotalValue = useMemo(
    () => cartEntries.reduce((sum, entry) => sum + entry.prod.price * entry.qty, 0),
    [cartEntries]
  );

  const navigate = (to) => {
    setStack((s) => [...s, route]);
    setRoute(to);
  };
const goHome = () => {
  setStack([]);
  setRoute({ name: "home" });
};
const goBack = () => {
  setStack((s) => {
    if (s.length === 0) {
      setRoute({ name: "home" });
      return s;
    }
    const prev = s[s.length - 1];
    setRoute(prev);
    return s.slice(0, -1);
  });
};
  const handleSelectCategory = (id) => {
    setSearchTerm("");
    navigate({ name: "cat", id });
  };

  const addToCart = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const inc = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart((c) => {
    const qty = (c[id] || 0) - 1;
    const copy = { ...c };
    if (qty <= 0) delete copy[id]; else copy[id] = qty;
    return copy;
  });
  const remove = (id) => setCart((c) => { const copy = { ...c }; delete copy[id]; return copy; });
  const clear = () => setCart({});

  const placeOrder = async ({ email, zip, city, street, house, address, paymentMethod }) => {
    const paymentFee = paymentMethod === "cod" ? COD_FEE : 0;
    const totalWithFee = cartTotalValue + paymentFee;
    const items = cartEntries.map(({ id, qty, prod }) => ({
      id,
      name: prod.name,
      price: prod.price,
      qty,
      lineTotal: prod.price * qty,
    }));
  const payload = {
    email,
    address: { zip, city, street, house, line: address },
    payment: {
      method: paymentMethod,
        fee: paymentFee,
        bankAccount: paymentMethod === "transfer" ? BANK_ACCOUNT : undefined,
      },
      items,
      subtotal: cartTotalValue,
    total: totalWithFee,
    status: "uj",
    createdAt: new Date().toISOString(),
  };
  try {
      let saved = null;
      try {
        saved = await createOrderRecord(payload);
      } catch (err) {
        console.error("Order submission failed (Firestore):", err);
        alert("A rendelés mentése nem sikerült. Kérjük, próbáld újra.");
        const pdfBlob = await generateOrderPdf({
          ...payload,
          id: `helyi-${Date.now()}`,
          total: totalWithFee,
        });
        const url = URL.createObjectURL(pdfBlob);
        setLastOrderPdf({
          url,
          name: "mngkp-rendeles.pdf",
        });
        setLastOrderInfo({
          id: `helyi-${Date.now()}`,
          bankAccount: BANK_ACCOUNT,
        });
        setLastOrderReady(true);
        setCart({});
        setOrderOpen(false);
        return;
      }
      const paymentMessage =
        paymentMethod === "transfer"
          ? `Utald át a végösszeget. A számla és közlemény adatait lent találod.`
          : `Utánvét díja: +${formatHUF(paymentFee)}. Fizetendő összesen: ${formatHUF(totalWithFee)}.`;
      const orderIdForMessage = saved?.id || "(n/a)";
      alert(`Köszönjük a megrendelést! Küldtünk egy visszaigazoló e-mailt.
Szállítás: ${address}
${paymentMessage}`);
      const pdfBlob = await generateOrderPdf({
        ...payload,
        id: saved?.id,
        total: totalWithFee,
      });
      const url = URL.createObjectURL(pdfBlob);
      setLastOrderPdf({
        url,
        name: `mngkp-rendeles-${saved?.id || "osszegzes"}.pdf`,
      });
      setLastOrderInfo({
        id: saved?.id || orderIdForMessage,
        bankAccount: BANK_ACCOUNT,
      });
      setLastOrderReady(true);
      setCart({});
      setOrderOpen(false);
  } catch (err) {
      console.error("Order submission failed (Firestore):", err);
      alert("A rendelés mentése nem sikerült. Kérjük, próbáld újra.");
    }
  };

  useEffect(() => {
    const handler = (e) => {
      setCart({});
    };
    window.addEventListener('order-submitted', handler);
    return () => window.removeEventListener('order-submitted', handler);
  }, []);

  return (
    <div className="min-h-dvh bg-neutral-50 text-neutral-900 relative">
      <BackgroundArt />
      <Header
        categories={categories}
        searchValue={searchTerm}
        onSearch={handleSearchSubmit}
        onSelectCategory={handleSelectCategory}
        onOpenContact={() => setContactOpen(true)}
      onGoHome={goHome}
      onGoCart={() => navigate({ name: "cart" })}
      cartCount={cartCount(cart)}
      user={user}
      showAdmin={isAdminUser}
      onAdminAccess={handleAdminAccess}
      onLogin={handleGoogleLogin}
      onLogout={handleGoogleLogout}
    />

      {route.name === "home" && <Home />}
      {route.name === "cat" && (
        <ProductListing
          categoryId={route.id}
          categories={categories}
          products={getProductsForCategory(route.id)}
          searchTerm={searchTerm}
          onSelectCategory={handleSelectCategory}
          onViewProduct={(id) => navigate({ name: "product", id })}
          onAddToCart={(id) => addToCart(id)}
        />
      )}
      {route.name === "product" && (
        <ProductDetail
          product={productsById.get(route.id)}
          onBack={goBack}
          onAdd={() => { addToCart(route.id); alert("A termék a kosárba került"); }}
        />
      )}
      {route.name === "cart" && (
        <CartScreen
          items={cartEntries}
          total={cartTotalValue}
          onBack={goBack}
          onInc={inc}
          onDec={dec}
          onRemove={remove}
          onClear={clear}
          onOrder={() => setOrderOpen(true)}
          onDownloadPdf={() => {
            if (lastOrderPdf?.url) {
              const link = document.createElement("a");
              link.href = lastOrderPdf.url;
              link.download = lastOrderPdf.name || "rendeles.pdf";
              link.click();
            } else {
              alert("Nincs elérhető rendelési PDF.");
            }
          }}
          canDownloadPdf={lastOrderReady && Boolean(lastOrderPdf?.url)}
          bankAccountInfo={lastOrderReady ? lastOrderInfo : null}
          lastOrderReady={lastOrderReady}
        />
      )}
      {route.name === "admin" && (
        <AdminPanel
          products={products}
          categories={categories}
          onBack={goBack}
          onHome={goHome}
          user={user}
          isAdmin={isAdminUser}
        />
      )}

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
      <OrderModal
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        cartTotal={cartTotalValue}
        onSubmit={({ email, zip, city, street, house, address, paymentMethod }) => {
          placeOrder({ email, zip, city, street, house, address, paymentMethod });
        }}
      />

      <footer className="footer mt-12">
        <div className="container py-8 text-sm text-neutral-600 flex flex-wrap items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} MNGKP</div>
          <div className="flex items-center gap-4">
            <a className="underline" href="/aszf">ÁSZF</a>
            <a className="underline" href="/adatvedelem">Adatvédelem</a>
            <a className="underline" href="/impresszum">Impresszum</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
